import { DeductionDetailJson } from "@/api/deduction.tsx"
import { complete_task, DeductionTask, TaskInfo, TaskStatus, TaskType } from "@/api/task.tsx"
import { format_reminders_sent, formatTaskType } from "@/deduction_detail/Timeline2.tsx"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { classes, get_display_name } from "@/utils"
import { format, parseISO } from "date-fns"

/** The task fields the card renders, common to the mirrored `deduction.task` JSON and the full `tasks` list. */
export interface PendingTaskView {
  id: string
  type: TaskType
  user_display_name?: string | null
  user_email?: string | null
  backup_type?: string | null
  note?: string | null
  due_date?: string | null
  is_time_sensitive?: boolean | null
  created_at?: string | null
  bump_count?: number | null
  last_bump_at?: string | null
}

/**
 * Pending tasks to surface at the top of the detail page, newest first. Prefers the full `tasks`
 * list from the detail payload; falls back to the single mirrored `task` JSON elsewhere.
 */
export function pending_tasks(deduction: { tasks?: DeductionTask[]; task?: TaskInfo }): PendingTaskView[] {
  if (deduction.tasks) {
    return deduction.tasks
      .filter(task => task.status === TaskStatus.Pending)
      .map(task => ({ ...task, type: task.task_type }))
  }
  if (deduction.task && deduction.task.status === TaskStatus.Pending) {
    return [deduction.task]
  }
  return []
}

async function handleTaskComplete(task_id: string) {
  const res = await complete_task(task_id)
  if (!res.ok) {
    console.error("Failed to mark task complete")
  }
  RefreshFlag.set(flag => !flag)
}

interface PendingTasksCardProps {
  deduction: DeductionDetailJson
  className?: string
}

/**
 * Prominent banner for pending tasks on the deduction. The same tasks also appear in the timeline,
 * but that is often below the fold — this card makes sure the assignee sees what their task is.
 */
export function PendingTasksCard({ deduction, className }: PendingTasksCardProps) {
  const tasks = pending_tasks(deduction)
  if (!tasks.length) {
    return null
  }
  return (
    <div id="pending-tasks" class={classes("border-t-6 border-amber-400 bg-amber-50 rounded-md p-4 space-y-2", className)}>
      <span class="flex">
        <div class="w-1 bg-amber-400 mr-2" />
        <h3 class="text-lg font-semibold leading-5">{tasks.length === 1 ? "Pending Task" : "Pending Tasks"}</h3>
      </span>
      <div class="flex flex-col gap-3">
        {tasks.map(task => {
          const assignee = task.user_display_name ?? get_display_name(task.user_email) ?? "Unassigned"
          const reminders = format_reminders_sent(task.bump_count, task.last_bump_at)
          return (
            <div class="flex gap-2 items-start">
              <div class="grow">
                <div class="font-medium">
                  {formatTaskType(task.type)}
                  {task.backup_type ? ` (${task.backup_type.toLowerCase()})` : ""}
                  {task.is_time_sensitive && (
                    <span class="ml-2 text-xs font-semibold text-red-600 bg-red-100 rounded px-1.5 py-0.5 align-middle">
                      Time sensitive
                    </span>
                  )}
                </div>
                <div class="text-gray-600 text-sm">
                  Assigned to {assignee}
                  {task.due_date ? ` · Due ${format(parseISO(task.due_date), "PP")}` : ""}
                </div>
                {task.note && <div class="text-gray-600 text-sm whitespace-pre-wrap">Note: {task.note}</div>}
                {reminders && <div class="text-gray-500 text-sm">{reminders}</div>}
              </div>
              <button
                onClick={() => handleTaskComplete(task.id)}
                class="py-2 px-4 bg-amber-500 hover:bg-amber-600 text-white rounded-md shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-opacity-50 cursor-pointer whitespace-nowrap">
                Mark Complete
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("pending_tasks", () => {
    function make_task(overrides: Partial<DeductionTask>): DeductionTask {
      return {
        id: "task-1",
        org_id: "org-1",
        user_id: "user-1",
        user_display_name: "Jane Doe",
        assigner_id: "user-2",
        assigner_display_name: "John Smith",
        task_type: TaskType.PullBackup,
        status: TaskStatus.Pending,
        created_at: "2026-08-01T12:00:00Z",
        updated_at: "2026-08-01T12:00:00Z",
        bump_count: 0,
        ...overrides,
      }
    }

    it("returns only pending tasks from the full tasks list", () => {
      const tasks = pending_tasks({
        tasks: [
          make_task({ id: "a", status: TaskStatus.Pending }),
          make_task({ id: "b", status: TaskStatus.Completed }),
          make_task({ id: "c", status: TaskStatus.Archived }),
        ],
      })
      expect(tasks).toHaveLength(1)
      expect(tasks[0].id).toBe("a")
      expect(tasks[0].type).toBe(TaskType.PullBackup)
    })

    it("prefers the tasks list over the mirrored task JSON", () => {
      const mirrored: TaskInfo = {
        id: "old",
        type: TaskType.Followup,
        status: TaskStatus.Pending,
        user_id: "user-1",
        created_at: "2026-08-01T12:00:00Z",
        assigner_email: "a@b.com",
        user_email: "c@d.com",
      }
      const tasks = pending_tasks({ tasks: [make_task({ id: "new" })], task: mirrored })
      expect(tasks).toHaveLength(1)
      expect(tasks[0].id).toBe("new")
    })

    it("falls back to the mirrored task JSON only when it is pending", () => {
      const mirrored: TaskInfo = {
        id: "old",
        type: TaskType.Followup,
        status: TaskStatus.Pending,
        user_id: "user-1",
        created_at: "2026-08-01T12:00:00Z",
        assigner_email: "a@b.com",
        user_email: "c@d.com",
      }
      expect(pending_tasks({ task: mirrored })).toHaveLength(1)
      expect(pending_tasks({ task: { ...mirrored, status: TaskStatus.Completed } })).toHaveLength(0)
      expect(pending_tasks({})).toHaveLength(0)
    })
  })
}
